/**
 * lib/useShopifySync.js
 * Otomatik Shopify sipariş senkronu — uygulama açıkken periyodik çalışır.
 *
 * Layout'ta admin/warehouse rollü kullanıcılar için mount edilir:
 *   - Uygulama açılışında bir kez (artımlı) senkron
 *   - Sonrasında her SYNC_INTERVAL_MS'de bir tekrar
 *   - Yeni sipariş geldiğinde toast bildirimi (→ otomatik depo emri)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { syncShopifyOrders, getLastSyncAt } from '@/lib/shopifySync';
import { logger } from '@/lib/logger';

const SYNC_INTERVAL_MS = 3 * 60 * 1000; // 3 dakika

export function useShopifySync({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [progressMsg, setProgressMsg] = useState('');
  const mountedRef = useRef(true);

  const runSync = useCallback(
    async ({ full = false, silent = false } = {}) => {
      setSyncing(true);
      setError(null);
      try {
        const result = await syncShopifyOrders({
          full,
          onProgress: (msg) => mountedRef.current && setProgressMsg(msg),
        });
        if (!mountedRef.current) return result;

        setLastResult({ ...result, at: new Date().toISOString() });

        if (result.imported > 0 || result.updated > 0 || result.cancelled > 0) {
          queryClient.invalidateQueries({ queryKey: ['orders'] });
        }
        if (result.relinked > 0) {
          // Geç eşleşen kalemler stok rezerve etti → ürün/stok görünümleri tazelensin
          queryClient.invalidateQueries({ queryKey: ['products'] });
          if (!silent) {
            toast.success(`🔗 ${result.relinked} Shopify-Position(en) nachträglich mit Produkten verknüpft`);
          }
        }
        if (result.imported > 0) {
          toast.success(
            `🛍️ ${result.imported} neue Shopify-Bestellung(en) importiert — Lagerauftrag erstellt`
          );
        }
        if (!silent && result.imported === 0 && !result.alreadyRunning) {
          toast.info('Shopify: keine neuen Bestellungen');
        }
        return result;
      } catch (err) {
        logger?.error?.('[useShopifySync]', err);
        if (mountedRef.current) setError(err.message);
        if (!silent) toast.error('Shopify Sync Fehler: ' + err.message);
        return null;
      } finally {
        if (mountedRef.current) {
          setSyncing(false);
          setProgressMsg('');
        }
      }
    },
    [queryClient]
  );

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) return undefined;

    // Açılışta artımlı senkron (hiç senkron yapılmadıysa tam senkron)
    const firstRun = setTimeout(() => {
      runSync({ full: !getLastSyncAt(), silent: true });
    }, 3000);

    const interval = setInterval(() => {
      runSync({ silent: true });
    }, SYNC_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearTimeout(firstRun);
      clearInterval(interval);
    };
  }, [enabled, runSync]);

  return { syncing, lastResult, error, progressMsg, runSync, lastSyncAt: getLastSyncAt() };
}

export default useShopifySync;
